import {EventEmitter} from 'events';
import {IPlayer} from '../IPlayer';
import {INCOMING_EVENTS, IncomingTransport} from './transports/incomingTransport';
import {OutgoingTransport} from './transports/outgoingTransport';

export const ERRORS = {
  NOT_ALLOWED_ERROR: 'NOT_ALLOWED_ERROR',
  NOT_SUPPORTED_ERROR: 'NOT_SUPPORTED_ERROR',
  MEDIA_ERR_ABORTED: 'MEDIA_ERR_ABORTED',
  MEDIA_ERR_NETWORK: 'MEDIA_ERR_NETWORK',
  MEDIA_ERR_DECODE: 'MEDIA_ERR_DECODE',
  MEDIA_ERR_SRC_NOT_SUPPORTED: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  UNKNOWN_PLAY_ERROR: 'UNKNOWN_ERROR',
  PLAY_ABORTED_ERROR: 'PLAY_ABORTED_ERROR',
};

/** *
 * Remote player that uses Audio player in other window. Communication done using two one directional transports.
 */
export class RemotePlayer extends EventEmitter implements IPlayer {
  public inited: boolean;
  public currentTime: number;
  public paused: boolean;
  private __src: undefined | string;
  private __previousSrc: undefined | string;
  private __currentTrackDuration: number;
  private outgoingTransport: OutgoingTransport;
  private incomingTransport: IncomingTransport;

  constructor({
    originalComponentId,
    outgoingTransport,
    incomingTransport,
  }: {
    originalComponentId: string;
    outgoingTransport?: OutgoingTransport;
    incomingTransport?: IncomingTransport;
  }) {
    super();
    this.__src = undefined;
    this.__previousSrc = undefined;
    this.__currentTrackDuration = 0;
    this.inited = false;
    this.currentTime = 0;
    this.paused = true;

    this.outgoingTransport = outgoingTransport || new OutgoingTransport(originalComponentId);
    this.incomingTransport = incomingTransport || new IncomingTransport(originalComponentId);

    this.incomingTransport.on(INCOMING_EVENTS.PLAYING, ({trackId}) => {
      this.paused = false;
      this.emit('playing', trackId);
    });
    this.incomingTransport.on(INCOMING_EVENTS.PAUSE, ({trackId}) => {
      this.paused = true;
      this.emit('pause', trackId);
    });
    this.incomingTransport.on(INCOMING_EVENTS.TIMEUPDATE, ({currentTime, trackId}) => {
      this.__timeUpdateHandler(currentTime, trackId);
    });
    this.incomingTransport.on(INCOMING_EVENTS.INIT, ({currentTime, trackId, paused}) => {
      this.currentTime = currentTime || 0;
      this.src = trackId;
      this.paused = paused;
      this.inited = true;
      this.emit('init', {currentTime, trackId});
    });
    this.incomingTransport.on(INCOMING_EVENTS.ERROR, (error) => {
      const knownErrorName = {
        NotAllowedError: ERRORS.NOT_ALLOWED_ERROR,
        NotSupportedError: ERRORS.NOT_SUPPORTED_ERROR,
        AbortError: ERRORS.PLAY_ABORTED_ERROR,
      }[error.name];
      this.emit('error', knownErrorName || error.name);
    });
  }

  get src() {
    return this.__src;
  }

  set src(value) {
    const oldValue = this.__src;
    this.__previousSrc = oldValue !== value ? oldValue : undefined;
    this.__src = value;
    if (!value || oldValue === value || !oldValue) {
      return;
    }
    this.currentTime = 0;
    this.emit('timeupdate', {currentTime: this.currentTime, trackId: value});
    if (this.paused) {
      this.outgoingTransport.emitChangeTrack({trackId: value});
    } else {
      this.outgoingTransport.emitPlaying({trackId: value});
    }
  }

  get currentTrackDuration() {
    return this.__currentTrackDuration;
  }

  set currentTrackDuration(value) {
    this.__currentTrackDuration = value;
  }

  init(): Promise<void> {
    if (this.inited) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      this.outgoingTransport.emitInit();
      this.once('init', resolve);
      this.once('error', reject);
    });
  }

  play() {
    this.outgoingTransport.emitPlaying({trackId: this.src});
  }

  pause() {
    this.outgoingTransport.emitPause({trackId: this.src});
  }

  updateCurrentTime(value) {
    const currentTime = Math.min(value, this.currentTrackDuration);
    this.outgoingTransport.emitTimeUpdate({trackId: this.src, currentTime});
    this.__timeUpdateHandler(value);
  }

  __timeUpdateHandler(currentTime, trackId?) {
    trackId = trackId || this.src;

    if (this.__previousSrc && this.__previousSrc === trackId) {
      return;
    }
    this.currentTime = currentTime;
    this.__previousSrc = undefined;
    this.__src = trackId;
    this.emit('timeupdate', {currentTime, trackId});
  }
}
